/**
 * Password Vault sul computer, ridisegnato il 26/10/2026 (os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: dov'è l'accesso di X?
 * La forma: una ricerca grande in cima e le voci come tessere col logo del
 * servizio (Google, Meta, Stripe, Hostinger, Cloudflare, GoDeal...), raggruppate
 * per cosa sono. Su ogni tessera i gesti che servono: copia la password senza
 * mostrarla, mostra, apri il sito, copia l'utente.
 *
 * Alleggerito (Lorenzo, 26/10): via i quattro numeri in testa, "Ordina" e
 * "Filtra per" (le voci sono poche decine e si trovano cercando), le colonne
 * Owner e Ultimo accesso (stanno nel suggerimento della tessera). I filtri
 * vecchi nell'indirizzo (categoria, solo) valgono ancora. Sul telefono resta
 * VaultMobile.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { toast } from '../../stores/toast.store';
import { Icon } from '../../components/ui/Icon';
import { BrandLogo, brandFor } from '../../components/ui/BrandLogo';
import { SectionMark } from '../../components/ui/SectionMark';
import { EmptyState } from '../../components/ui/EmptyState';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { useFiltri, type FiltroDef } from '../../components/ui/OrdinaFiltra';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtDate } from '../../lib/utils';
import { ModuleKey, hasAtLeast, PermissionLevel } from '@masamune/shared';
import './vault-desktop.css';

export interface VaultEntry {
  id: string; label: string; category: string; username: string | null; url: string | null;
  owner: { firstName: string; lastName: string } | null;
  allowedRoleIds: string[]; lastAccessAt: string | null;
  expiresAt: string | null; createdAt: string; updatedAt: string;
}

/** Le categorie in parole (prima "ADV PLATFORM", "HOSTING DOMINIO" in maiuscolo). */
export const CATEGORIA: Record<string, string> = {
  PIATTAFORMA_CLIENTE: 'Piattaforme per i clienti',
  ADV_PLATFORM: 'Pubblicità online',
  HOSTING_DOMINIO: 'Siti e domini',
  INFRASTRUTTURA: 'Server e infrastruttura',
  AMMINISTRATIVA: 'Banca, pagamenti e fatture',
  DESIGN_TOOL: 'Strumenti di grafica',
  ALTRO: 'Altro',
};
const ORDINE_CATEGORIE = ['AMMINISTRATIVA', 'PIATTAFORMA_CLIENTE', 'ADV_PLATFORM', 'HOSTING_DOMINIO', 'INFRASTRUTTURA', 'DESIGN_TOOL', 'ALTRO'];
export const nomeCategoria = (c: string) => CATEGORIA[c] ?? c.replace(/_/g, ' ').toLowerCase().replace(/^./, (x) => x.toUpperCase());

/** Il servizio è la prima parte del nome ("Stripe · chiave segreta" → "Stripe"): serve al logo. */
const servizio = (v: VaultEntry) => v.label.split(/\s+[·•\-–]\s+/)[0] ?? v.label;
const dettaglio = (v: VaultEntry) => { const i = v.label.search(/\s+[·•\-–]\s+/); return i > 0 ? v.label.slice(i).replace(/^\s+[·•\-–]\s+/, '') : null; };

export function VaultDesktop() {
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [mostrate, setMostrate] = useState<Record<string, string | undefined>>({});

  const canAccess = !!user && hasAtLeast(user.permissions.find((p) => p.module === ModuleKey.VAULT)?.level ?? PermissionLevel.NONE, PermissionLevel.READ);
  // pageSize=200: la lista non ha pagine, quindi va chiesta tutta (senza, l'API ne manda 50).
  const { data, isLoading } = useQuery({
    queryKey: ['vault', search],
    queryFn: () => api.get<{ items: VaultEntry[] }>(`/vault?pageSize=200${search ? `&search=${encodeURIComponent(search)}` : ''}`),
    enabled: canAccess,
  });
  const { data: tutte } = useQuery({ queryKey: ['vault', 'stats'], queryFn: () => api.get<{ items: VaultEntry[] }>('/vault?pageSize=200'), enabled: canAccess });

  // I filtri di prima, senza comandi a schermo: dall'indirizzo valgono ancora.
  const defs = useMemo<FiltroDef<VaultEntry>[]>(() => [
    { param: 'categoria', label: 'Categoria', multi: true, opzioni: Object.keys(CATEGORIA).map((c) => ({ value: c, label: nomeCategoria(c) })), test: (v, c) => v.category === c },
    { param: 'solo', label: 'Quali voci', opzioni: [{ value: 'scadenza', label: 'con una scadenza' }, { value: 'senza-owner', label: 'senza un responsabile' }], test: (v, x) => (x === 'scadenza' ? !!v.expiresAt : !v.owner) },
  ], []);
  const filtri = useFiltri(defs, data?.items);
  const voci = useMemo(() => filtri.applica(data?.items ?? []), [data, filtri]);
  const gruppi = useMemo(() => {
    const m = new Map<string, VaultEntry[]>();
    for (const v of voci) m.set(v.category, [...(m.get(v.category) ?? []), v]);
    for (const [, arr] of m) arr.sort((a, b) => a.label.localeCompare(b.label, 'it'));
    const pos = (c: string) => { const i = ORDINE_CATEGORIE.indexOf(c); return i < 0 ? 99 : i; };
    return [...m.entries()].sort(([a], [b]) => pos(a) - pos(b));
  }, [voci]);

  const rivela = useMutation({
    mutationFn: (id: string) => api.post<{ password: string }>(`/vault/${id}/reveal`),
    onSuccess: (res, id) => setMostrate((r) => ({ ...r, [id]: res.password })),
    onError: () => toast.alert('Password non mostrata', 'Riprova fra poco.'),
  });
  // Copia senza mostrare: passa dallo stesso /reveal, così l'accesso resta nel registro.
  const copiaPassword = async (id: string) => {
    try {
      const pwd = mostrate[id] ?? (await api.post<{ password: string }>(`/vault/${id}/reveal`)).password;
      await navigator.clipboard.writeText(pwd);
      toast.ok('Password copiata', 'Incollala dove serve: a schermo non compare.');
    } catch {
      toast.alert('Copia non riuscita', 'Il browser non lascia scrivere negli appunti.');
    }
  };
  const copiaUtente = async (u: string) => {
    try { await navigator.clipboard.writeText(u); toast.ok('Utente copiato'); } catch { toast.alert('Copia non riuscita', 'Il browser non lascia scrivere negli appunti.'); }
  };

  if (!canAccess) {
    return (
      <div className="page">
        <div className="max-w-[480px] mx-auto py-20">
          <EmptyState icon="lock" title="Password Vault" description={<>È riservato a chi ha il permesso del Vault.{user && <> Il tuo ruolo: <b>{user.role.name}</b>.</>}</>} />
        </div>
      </div>
    );
  }

  const n = tutte?.items.length ?? 0;
  const conScadenza = (tutte?.items ?? []).filter((v) => v.expiresAt).length;
  const accesi = filtri.attivi > 0 ? defs.flatMap((d) => d.opzioni.filter((o) => filtri.ha(d.param, o.value)).map((o) => o.label)) : [];

  return (
    <div className="page va2">
      <header className="va2-hd">
        <div className="va2-hd-main">
          <SectionMark icon="lock" className="va2-sez" />
          <div className="min-w-0">
            <h1 className="va2-title">Password Vault</h1>
            <p className="va2-risposta">
              {!tutte ? 'Apro il Vault…'
                : <>Ci sono <strong>{n} {n === 1 ? 'accesso salvato' : 'accessi salvati'}</strong>, cifrati: ogni volta che si apre una password resta scritto chi l’ha vista.{conScadenza > 0 && <> {conScadenza === 1 ? 'Uno ha' : `${conScadenza} hanno`} una scadenza.</>}</>}
            </p>
          </div>
        </div>
        <div className="va2-hd-azioni">
          <button className="btn btn-primary" onClick={() => openModal('newVault')}><Icon name="plus" /> Nuova voce</button>
          <ActionsMenu variant="ghost" items={[{ key: 'export', label: 'Esporta il registro degli accessi', icon: 'export', onClick: () => openModal('export', { what: 'Audit log' }) }]} />
        </div>
      </header>

      <label className="va2-search">
        <Icon name="search" size={16} />
        <input autoFocus aria-label="Cerca un accesso" placeholder="Cerca un servizio, un utente o una categoria: Stripe, Google, Hostinger…" value={search} onChange={(e) => setSearch(e.target.value)} />
        {search && <button type="button" className="va2-search-x" aria-label="Svuota la ricerca" onClick={() => setSearch('')}><Icon name="x" size={14} /></button>}
      </label>

      {accesi.length > 0 && (
        <div className="va2-filtro"><Icon name="filter" size={12} /> Stai vedendo solo le voci {accesi.join(', ')}. <button type="button" onClick={filtri.clear}>Mostra tutte</button></div>
      )}

      {isLoading && !data ? (
        <div className="va2-griglia">{[0, 1, 2, 3].map((i) => <div key={i} className="va2-tessera"><span className="skeleton sk-block" /></div>)}</div>
      ) : voci.length === 0 ? (
        <EmptyState
          brand
          title={search || accesi.length ? 'Nessun accesso trovato' : 'Il Vault è vuoto'}
          description={search || accesi.length ? 'Prova con un’altra parola.' : 'Salva qui gli accessi condivisi: server, servizi, integrazioni. Tutto cifrato e registrato.'}
          action={!search && !accesi.length ? <button className="btn btn-primary" onClick={() => openModal('newVault')}><Icon name="plus" size={12} /> Salva il primo accesso</button> : undefined}
        />
      ) : (
        gruppi.map(([cat, vs]) => (
          <section key={cat} className="va2-gruppo">
            <h2 className="va2-gruppo-t">{nomeCategoria(cat)}<span className="n">{vs.length}</span></h2>
            <div className="va2-griglia">
              {vs.map((v) => {
                const mostrata = mostrate[v.id];
                const scade = v.expiresAt ? Math.round((new Date(v.expiresAt).getTime() - Date.now()) / 86400000) : null;
                const chi = v.owner ? `${v.owner.firstName} ${v.owner.lastName}` : null;
                const info = [chi && `responsabile: ${chi}`, v.lastAccessAt && `ultima apertura: ${fmtDate(v.lastAccessAt)}`].filter(Boolean).join(' · ');
                return (
                  <article key={v.id} className="va2-tessera" title={info || undefined}>
                    <div className="va2-tessera-hd">
                      <BrandLogo brand={brandFor(servizio(v), v.url)} name={servizio(v)} size={36} />
                      <div className="min-w-0">
                        <div className="t">{servizio(v)}</div>
                        {dettaglio(v) && <div className="s">{dettaglio(v)}</div>}
                      </div>
                      {v.url && (
                        <a className="va2-ico" href={v.url} target="_blank" rel="noreferrer" title={`Apri ${v.url}`} aria-label={`Apri il sito di ${servizio(v)}`}>
                          <Icon name="arrowRight" size={14} />
                        </a>
                      )}
                    </div>
                    <div className="va2-campi">
                      <div className="va2-campo">
                        <span className="k">Utente</span>
                        {v.username
                          ? <button type="button" className="v mono va2-copia" title="Copia l’utente" onClick={() => copiaUtente(v.username!)}>{v.username}</button>
                          : <span className="v va2-manca">nessuno</span>}
                      </div>
                      <div className="va2-campo">
                        <span className="k">Password</span>
                        <span className={`v mono${mostrata ? '' : ' va2-punti'}`}>{mostrata ?? '••••••••••'}</span>
                        <span className="va2-gesti">
                          <button type="button" className="va2-ico" title={mostrata ? 'Nascondi' : 'Mostra'} aria-label={mostrata ? 'Nascondi la password' : 'Mostra la password'} onClick={() => (mostrata ? setMostrate((r) => ({ ...r, [v.id]: undefined })) : rivela.mutate(v.id))}>
                            <Icon name={mostrata ? 'eyeOff' : 'eye'} size={14} />
                          </button>
                          <button type="button" className="va2-ico is-forte" title="Copia la password senza mostrarla" aria-label="Copia la password senza mostrarla" onClick={() => copiaPassword(v.id)}>
                            <Icon name="copy" size={14} />
                          </button>
                        </span>
                      </div>
                    </div>
                    {scade != null && (
                      <div className={`va2-scade${scade < 30 ? ' is-presto' : ''}`}>
                        <Icon name="calendarClock" size={12} /> {scade < 0 ? `scaduta il ${fmtDate(v.expiresAt)}` : `scade il ${fmtDate(v.expiresAt)}`}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
